import { mock$beFindReaderV1 } from 'actions/mock/barnard.mock';
import { mock$beFindReaderV3 } from 'actions/mock/castor.mock';
import H from 'constants/httpMethod.const';
import POS_READER_STATUS from 'constants/pos/posReaderStatus.const';
import UMSG_TYPE from 'constants/umsg/type.const';
import asArray from 'utils/arrays/asArray.util';
import mockJsonResponse from 'utils/backend/mockJsonResponse.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBoolUtil from 'utils/converters/fromAnyToBool.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import { isEnvDev } from 'utils/env/determine.util';
import actionEndpointBe from 'utils/mw/actionEndpointBe.util';

// SEE: https://github.com/getslick/api.core/blob/staging/docs/pos.md

const IS_DEV = isEnvDev();

// eslint-disable-next-line no-unused-vars
const MOCK_V1 = Object.freeze({
  online: () => mockJsonResponse({ body: { reader: 'some-reader-id', reader_status: 'online' } }),
  error1: () => mockJsonResponse({ body: { error: 'error1 message' } }),
  error2: () => mockJsonResponse({ body: { errorMessage: 'error2 message' } }),
});

// eslint-disable-next-line no-unused-vars
const MOCK_V3 = Object.freeze({
  mix: () =>
    mockJsonResponse({
      body: {
        results: {
          readers: [
            {
              id: 4,
              reader: 'reader-id--04',
              status: POS_READER_STATUS.inactive,
              is_main: false,
              name: 'Fourth Reader Inactive',
              type: null,
              stylist: '',
              stylist_id: null,
              location_id: 'location-id--04',
            },
            {
              id: 1,
              reader: 'reader-id--01',
              status: POS_READER_STATUS.online,
              is_main: false,
              name: 'First Reader Online',
              type: null,
              stylist: '',
              stylist_id: null,
              location_id: 'location-id--01',
            },
            {
              id: 3,
              reader: 'reader-id--03',
              status: POS_READER_STATUS.online,
              is_main: true,
              name: 'Third/Main Reader Online',
              type: null,
              stylist: '',
              stylist_id: null,
              location_id: 'location-id--03',
            },
            {
              id: 2,
              reader: 'reader-id--02',
              status: POS_READER_STATUS.offline,
              is_main: false,
              name: 'Second Reader Offline',
              type: null,
              stylist: '',
              stylist_id: null,
              location_id: 'location-id--02',
            },
            {
              id: 5,
              reader: 'reader-id--05',
              status: POS_READER_STATUS.ordered,
              is_main: false,
              name: 'Fifth Reader Ordered',
              type: null,
              stylist: '',
              stylist_id: null,
              location_id: 'location-id--05',
            },
          ],
        },
        _v: 3,
      },
    }),
  noReader: () => mockJsonResponse({ body: { _v: 3, results: { reader_status: POS_READER_STATUS.noReader } } }),
});

export const beFindReaderV1 = actionEndpointBe('beFindReaderV1', H.get, 'payments/stripe/get/reader/:salonId/', (options) => ({
  ...options,
  inert: true,
  mock: IS_DEV ? mock$beFindReaderV1 : void 1,
}));

const fromAnyToId = (id) => (id ? fromAnyToInteger(id) : id);

const fromDtoToPosReader = (dto) => {
  const reader = fromAnyToTrimmed(dto?.reader);
  const status = fromAnyToTrimmed(dto?.status);

  const isOnline = POS_READER_STATUS.online === status;
  const isOrdered = POS_READER_STATUS.ordered === status;
  const isMissing = POS_READER_STATUS.noReader === status;
  const isOffline = POS_READER_STATUS.offline === status;

  // NOTE: there is a difference between being offline and being not online
  const isActive = isOnline || isOffline;

  return {
    // fields that align with the older version of beFindReader result and determinePosReaderId
    // SEE: src/bl/pos/determinePosReaderId.bl.js
    readerId: reader,
    readerStatus: status,
    isOrdered,
    isMissing,
    isOnline,
    isNotOnline: !isOnline,

    // regular fields from the newer body of beFindReaderV3 result
    // SEE: https://daysmartsoftware.atlassian.net/browse/GS-461
    isActive,
    status,
    reader,
    id: dto?.id ? fromAnyToInteger(dto?.id) : dto?.id,
    name: fromAnyToTrimmed(dto?.name),
    isMain: fromAnyToBoolUtil(dto?.is_main),
    type: dto?.type,
    stylist: dto?.stylist,
    stylistId: fromAnyToId(dto?.stylistId) || fromAnyToId(dto?.stylist_id),
    locationId: fromAnyToTrimmed(dto?.location_id),
  };
};

const fromDtoToPosReaders = (dto) => {
  const generalReaderStatus = fromAnyToTrimmed(dto?.results?.reader_status);
  return generalReaderStatus ? { generalReaderStatus } : asArray(dto?.results?.readers).map(fromDtoToPosReader);
};

export const beFindReaderV3 = actionEndpointBe('beFindReaderV3', H.get, 'payments/v3/stripe/get/readers/:salonId/', (options) => ({
  ...options,
  inert: true,
  fromDto: fromDtoToPosReaders,
  umsg: UMSG_TYPE.global,
  mock: IS_DEV ? mock$beFindReaderV3 : void 1,
}));
